/**
 * Reading a delimited file a person actually saved out of Excel.
 *
 * A naive `split(',')` is wrong here in a way that does not announce itself: colour names contain
 * commas — `CTE 002, PEWTER` — and splitting on them shifts every later column, so the mapping ends
 * up pointing at the wrong parent. The file is read INCORRECTLY rather than rejected, which is the
 * worst of the available outcomes.
 *
 * So: quoted fields, doubled quotes inside them (Excel's escape), CRLF, blank lines, and the UTF-8
 * byte-order mark Excel writes — without which the first header reads `<BOM>child_sku` and never
 * matches anything.
 *
 * Tab-separated works too, because pasting a spreadsheet selection is the other way people do this.
 *
 * ⚠️ A quote is special ONLY as the first character of a field. LSK's codes carry inch marks
 * (`BFJOY182(10")FB1AB`) and treating those as opening quotes merged columns silently — the row
 * still looked like a row, just with the wrong values in it.
 */

/** One row per line, one string per field. Quotes are removed; content inside them is preserved. */
export function parseDelimited(text: string): string[][] {
  const src = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let sawQuote = false;

  const endField = () => {
    // Only trim an UNQUOTED field: spaces inside quotes were put there on purpose.
    row.push(sawQuote ? field : field.trim());
    field = '';
    sawQuote = false;
  };
  const endRow = () => {
    endField();
    if (row.some((c) => c !== '')) rows.push(row);
    row = [];
  };

  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"' && field === '' && !sawQuote) {
      // A quote is special ONLY at the start of a field. LSK's item codes are full of inch marks
      // — `BFJOY182(10")FB1AB`, `DIVAN 182X190X8"` — and treating one of those as an opening quote
      // swallowed the delimiter after it, welding two columns into one and turning the piece
      // letter into the parent code.
      quoted = true;
      sawQuote = true;
    } else if (c === ',' || c === '\t') {
      endField();
    } else if (c === '\n') {
      endRow();
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length > 0) endRow();
  return rows;
}

/** Column order for each mapping kind, and what the template hands out. */
export const CSV_COLUMNS = {
  colour: ['child_sku', 'parent_sku', 'colour_code', 'colour_name'],
  // `colour_name` is OPTIONAL and used only to NAME a piece when its product is created in
  // Smartlink. Every four-column file already in use still parses unchanged.
  split: ['child_sku', 'parent_sku', 'piece', 'qty_per_set', 'colour_code', 'colour_name'],
} as const;

/**
 * Turn parsed rows into mapping rows, skipping a header line if one is present.
 *
 * The header is optional because a spreadsheet PASTE usually has none while a saved CSV usually
 * does. Detecting it beats demanding it: a file rejected for a missing header teaches people to
 * delete the header, which is the opposite of helpful.
 */
export function toMappingRows(
  rows: string[][],
  kind: 'colour' | 'split',
): Array<Record<string, string>> {
  const cols = CSV_COLUMNS[kind];
  const looksLikeHeader =
    rows[0] !== undefined && rows[0][0] !== undefined && /^child[_ ]?sku$/i.test(rows[0][0].trim());
  return rows.slice(looksLikeHeader ? 1 : 0).map((r) => {
    const out: Record<string, string> = {};
    cols.forEach((name, i) => {
      out[name] = r[i] ?? '';
    });
    return {
      childSku: out['child_sku'] ?? '',
      parentSku: out['parent_sku'] ?? '',
      ...(kind === 'colour'
        ? { colourCode: out['colour_code'] ?? '', colourName: out['colour_name'] ?? '' }
        : {
            piece: out['piece'] ?? '',
            qtyPerSet: out['qty_per_set'] ?? '',
            colourCode: out['colour_code'] ?? '',
            colourName: out['colour_name'] ?? '',
          }),
    };
  });
}
